import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Refresh } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, Dialog, DialogContent, Divider, Link, Skeleton, Stack, Typography } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { GameIcon } from '../../../../../features/game'
import { borderRadiusDefault } from '../../../../../themes/theme'
import { useGetAdDataVersionInfoQuery, useRequestMetadataAnalysisMutation } from '../../../../api/ad-data-tester'
import { toSafeHttpUrl } from '../../../../helpers/safeUrl'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { VisualAttributeBlock } from '../VisualAttributeBlock/VisualAttributeBlock'
import { AdDataRow, buildAttributeGroups } from '../helpers/adDataHelpers'

type AdDataAnalysisDialogProps = {
  row?: AdDataRow
  subgenres: { id: string; name: string }[]
  onClose: () => void
}

export const AdDataAnalysisDialog: FC<AdDataAnalysisDialogProps> = ({ row, subgenres, onClose }) => {
  const { t } = useTranslation()
  const { run } = useMutationFeedback()
  const versionQuery = useGetAdDataVersionInfoQuery({ appId: row?.appId ?? 0, marketIso: row?.market ?? '' }, { skip: !row })
  const [requestMetadataAnalysis, { isLoading: isRequesting }] = useRequestMetadataAnalysisMutation()
  const [requestedAppId, setRequestedAppId] = useState<number>()
  const [showFullDescription, setShowFullDescription] = useState(false)

  useEffect(() => setShowFullDescription(false), [row?.appId])

  const groups = useMemo(() => (row ? buildAttributeGroups(row.aggregate, subgenres) : []), [row, subgenres])
  const versionInfo = versionQuery.data
  const screenshots = useMemo(() => (versionInfo?.screenshots ?? []).flatMap((url) => toSafeHttpUrl(url) ?? []), [versionInfo?.screenshots])

  const requestAnalysis = useCallback(async () => {
    if (!row) return
    const requested = await run(requestMetadataAnalysis({ appId: row.appId, market: row.market }).unwrap(), {
      success: t('internal-service-management:ad_data_update_sent'),
      error: t('internal-service-management:ad_data_update_failed'),
    })
    if (requested.ok) setRequestedAppId(row.appId)
  }, [requestMetadataAnalysis, row, run, t])

  return (
    <Dialog open={!!row} onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="ad-data-analysis-title">
      {row && (
        <>
          <GRDialogTitle onClose={onClose} id="ad-data-analysis-title">
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between" flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
                <GameIcon src={row.icon ?? ''} gameName={row.name} size="small" />
                <Typography variant="h3" noWrap>
                  {t('internal-service-management:ad_data_analysis_title', { name: row.name })}
                </Typography>
              </Stack>
              <LoadingButton
                variant="outlined"
                startIcon={<Refresh />}
                loading={isRequesting}
                disabled={requestedAppId === row.appId}
                onClick={requestAnalysis}
                sx={{ flexShrink: 0 }}
              >
                {t('internal-service-management:ad_data_update_metadata')}
              </LoadingButton>
            </Stack>
          </GRDialogTitle>
          <DialogContent dividers>
            {versionQuery.isFetching ? (
              <Stack direction="row" spacing={1} mb={2}>
                {[0, 1, 2, 3].map((index) => (
                  <Skeleton key={index} variant="rectangular" width={120} height={200} />
                ))}
              </Stack>
            ) : versionQuery.isError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('internal-service-management:ad_data_version_failed')}
              </Alert>
            ) : !versionInfo ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('internal-service-management:ad_data_version_missing', { market: row.market.toUpperCase() })}
              </Alert>
            ) : (
              <Box mb={2}>
                {screenshots.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }} aria-label={t('internal-service-management:ad_data_screenshots')}>
                    {screenshots.map((url, index) => (
                      <Link
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('internal-service-management:ad_data_open_screenshot', { number: index + 1 })}
                        sx={{ flexShrink: 0 }}
                      >
                        <Box component="img" src={url} alt="" sx={{ height: 200, borderRadius: borderRadiusDefault, display: 'block' }} />
                      </Link>
                    ))}
                  </Stack>
                )}
                {versionInfo.description && (
                  <>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', mt: 1, ...(showFullDescription ? {} : { maxHeight: 96, overflow: 'hidden' }) }}
                    >
                      {versionInfo.description}
                    </Typography>
                    <Button size="small" onClick={() => setShowFullDescription((shown) => !shown)} sx={{ mt: 0.5 }}>
                      {showFullDescription
                        ? t('internal-service-management:ad_data_hide_description')
                        : t('internal-service-management:ad_data_show_description')}
                    </Button>
                  </>
                )}
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />
            {groups.length === 0 ? (
              <Typography color="text.secondary">{t('internal-service-management:ad_data_no_analyses')}</Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                {groups.map((group) => (
                  <Box key={group.key} component="section" aria-label={t(`internal-service-management:ad_data_group_${group.key}`)}>
                    <Typography variant="h4" mb={1}>
                      {t(`internal-service-management:ad_data_group_${group.key}`)}
                    </Typography>
                    {group.attributes.map((attribute) => (
                      <VisualAttributeBlock key={attribute.name} attribute={attribute} isColor={group.isColor} />
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}
